import { Outlet, Route, Routes, Link, useLocation, resolvePath } from 'react-router-dom';
import { Tabs, Tab, Box, Typography, Stack, Chip, Tooltip } from '@mui/material';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import StorageIcon from '@mui/icons-material/Storage';
import GradingIcon from '@mui/icons-material/Grading';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined'

import { CompanyHome } from 'components/Companies/company-home';
import { CompanyLegalInfo } from 'components/Companies/LegalEntities/company-legal-info';
import { CompanyLeads } from 'components/Companies/LegalEntities/company-leads';
import { CompanyClientInvoices } from 'components/Companies/Invoices/company-client-invoices';
import { CompanySupplierInvoices } from 'components/Companies/Invoices/company-supplier-invoices';
import { useTitleHook } from 'hooks/page-title.hook';
import { PageHeader } from 'shared/ui/details-page-header';
import { useCompany } from 'hooks/companies.hook';
import { AuditEntityType, CompanyDto, UpdateCompanyDto } from '@sr/dto';
import { CompanyTypes } from 'components/Companies/company-type-span';
import { EditCompanyDialog } from 'components/Companies/CompanyDialog/company-dialog.edit';
import { useSnack } from 'shared/ui/snack';
import { useState, useMemo } from 'react';
import { ActionsMenu } from 'shared/ui/actions-menu';
import { ErrorBanner } from 'shared/ui/error-banner';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { useIdParam } from 'utils/url-paramters.hook';
import EditIcon from '@mui/icons-material/Edit';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { companyApi } from 'entities/company/details/api';
import { entityTagsApi } from 'entities/tag/api/entity-tags';
import { EditTagsDialog } from 'features/tag/edit-tags-dialog';
import { useEntityTags } from 'entities/tag/lib/entity-tags.hook';
import { EntityTags } from 'entities/tag/ui/entity-tags';
import { AuditLogSection } from 'entities/audit/ui/audit-log-section';
import { useAppDispatch } from 'shared/store';
import { invalidateEntityAudit } from 'entities/audit/utils';
import { CompanyMedia } from './company-media';

interface TabInfo {
  label: string;
  icon: JSX.Element;
}
const tabInfos = {
  home: {
    label: 'Контакты',
    icon: <StorageIcon />,
  },
  'legal-entities': {
    label: 'Юрлица',
    icon: <AccountBalanceIcon />,
  },
  'client-invoices': {
    label: 'Счета исходящие',
    icon: <ReceiptIcon />,
  },
  'supplier-invoices': {
    label: 'Счета входящие',
    icon: <MoveToInboxIcon />,
  },
  leads: {
    label: 'Лиды',
    icon: <MoveToInboxIcon />,
  },
  media: {
    label: 'СМИ',
    icon: <LiveTvOutlinedIcon />
  },
  audit: {
    label: 'Аудит',
    icon: <GradingIcon />,
  },
};

const filterTabs = (isClient: boolean, isSupplier: boolean, isCompetitor: boolean, isMedia: boolean) => {
  return Object.entries(tabInfos).filter(([key]) => {
    if (isCompetitor || isMedia) return true;
    if (isClient && isSupplier) return true;
    if (isClient) return key !== 'media';
    if (isClient) return key !== 'supplier-invoices';
    if (isSupplier) return key !== 'client-invoices';
    return true;
  });
};

export const CompanyDetailsPage = () => {
  const companyId = useIdParam('companyId');

  const { company, error, refetch } = useCompany(companyId);
  const isClientSupplier = company?.isClient && company?.isSupplier;

  const [updateCompany] = companyApi.useUpdateCompanyDetailsMutation();
  const [updateTags] = entityTagsApi.useUpdateCompanyTagsMutation();

  const { data: tagsData } = useEntityTags('company', companyId);
  const tags = tagsData ?? [];
  const selectedTags = tags.map((tag) => tag.id);
  const [isAddCompanyDialogOpen, setAddCompanyDialogOpen] = useState(false);
  const [isEditTagsDialogOpen, setEditTagsDialogOpen] = useState(false);
  const dispatch = useAppDispatch();

  const { showSuccess, showError } = useSnack();

  useTitleHook(company?.name);

  const handleUpdateCompany = async (companyData: UpdateCompanyDto) => {
    try {
      await updateCompany(companyData).unwrap();
      showSuccess('Компания успешно обновлена');
      invalidateEntityAudit(dispatch, AuditEntityType.COMPANY, companyId);
    } catch (error) {
      showError('Ошибка редактирования компании:', error);
    } finally {
      refetch();
    }
  };

  const handleTagsUpdate = async (newTagIds: (number | string)[]) => {
    try {
      await updateTags({ companyId, tagIds: newTagIds.map((id) => Number(id)) }).unwrap();
      showSuccess('Теги успешно обновлены');
    } catch (error) {
      showError('Ошибка обновления тегов:', error);
    } finally {
      refetch();
    }
  };

  if (error) return <ErrorBanner errorMessage={error} />;

  if (!company) return <LoadingBanner />;

  return (
    <>
      <PageHeader
        fallbackTo="/clients/list-client-all"
        preferFallbackTo
        title={
          <>
            <Stack direction="row" sx={{ alignItems: 'baseline' }} spacing={1}>
              <Typography variant="h5" color="primary">
                {company?.name}
              </Typography>

              <OkvedBadges okved={company.okved} />

              {company.city && (
                <Typography variant="body1" color="gray">
                  <b>г.{company?.city.name},</b> {company?.city?.region.name}, {company?.city?.region?.country.name}
                </Typography>
              )}
            </Stack>
            <Stack direction={'row'} spacing={2}>
              <CompanyTypes company={company} />
              <EntityTags tags={tags} displayLimit={4} />
            </Stack>
          </>
        }
        actions={
          <Stack direction="row" spacing={2} alignItems="center">
            <ActionsMenu
              actions={[
                {
                  callback: () => setAddCompanyDialogOpen(true),
                  label: 'Редактировать',
                  icon: <EditIcon />,
                },
                {
                  callback: () => setEditTagsDialogOpen(true),
                  label: 'Редактировать теги',
                  icon: <LocalOfferIcon />,
                },
              ]}
            />
          </Stack>
        }
        sx={{ mt: 1, mb: 0 }}
      />

      <CompanyTabs company={company} />

      <Routes>
        <Route element={<Outlet />}>
          <Route path="home" element={<CompanyHome company={company} />} />
          <Route path="legal-entities/*">
            <Route index element={<CompanyLegalInfo companyId={companyId} />} />
          </Route>
          <Route path="client-invoices" element={<CompanyClientInvoices companyId={companyId} isClientSupplier={isClientSupplier} />} />
          <Route path="supplier-invoices" element={<CompanySupplierInvoices companyId={companyId}/>} />
          <Route path="leads" element={<CompanyLeads companyId={companyId} />} />
          <Route path="media" element={<CompanyMedia companyId={companyId} isSupplier={company.isSupplier} />} />
          <Route path="audit" element={<AuditLogSection entityType={AuditEntityType.COMPANY} entityId={companyId} />} />
        </Route>
      </Routes>

      {company && <EditTagsDialog open={isEditTagsDialogOpen} onSave={handleTagsUpdate} onClose={() => setEditTagsDialogOpen(false)} selected={selectedTags} />}
      {company && <EditCompanyDialog open={isAddCompanyDialogOpen} onSubmit={handleUpdateCompany} onClose={() => setAddCompanyDialogOpen(false)} company={company} />}
    </>
  );
};

const OkvedBadges = ({ okved }: { okved?: CompanyDto['okved'] }) => {
  if (!okved || okved.length === 0) {
    return null;
  }

  const MAX_VISIBLE = 2;
  const visible = okved.slice(0, MAX_VISIBLE);
  const hidden = okved.slice(MAX_VISIBLE);
  const formatLabel = (item: CompanyDto['okved'][number]) => `${item.code} — ${item.name}`;

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5, mb: 1 }}>
      {visible.map((item) => (
        <Chip
          key={item.id ?? `${item.code}-${item.name}`}
          label={formatLabel(item)}
          size="small"
          variant="outlined"
          sx={{
            borderRadius: 1,
            fontSize: '0.75rem',
            maxWidth: 220,
            ml: '0 !important',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            },
          }}
        />
      ))}
      {hidden.length > 0 && (
        <Tooltip arrow placement="bottom-start" title={<Box sx={{ whiteSpace: 'pre-line' }}>{hidden.map((item) => formatLabel(item)).join('\n')}</Box>}>
          <Chip label={`+${hidden.length}`} size="small" color="primary" variant="outlined" sx={{ borderRadius: 1, fontWeight: 600 }} />
        </Tooltip>
      )}
    </Stack>
  );
};

const CompanyTabs = ({ company }: { company: CompanyDto }) => {
  const tabs = useMemo(() => {
    return filterTabs(company.isClient, company.isSupplier, company.isCompetitor, company.isMedia);
  }, [company]);
  const location = useLocation();
  const pathSplit = location.pathname.split('/');
  pathSplit.pop();
  const companyRoot = pathSplit.join('/');
  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
      <Tabs value={location.pathname}>
        {tabs.map(([route, info]: [string, TabInfo], i: number) => {
          const value = resolvePath(route, companyRoot).pathname;
          return (
            <Tab
              key={i}
              label={info.label}
              value={value}
              iconPosition="start"
              icon={info.icon}
              to={route}
              state={location.state}
              component={Link}
            />
          );
        })}
      </Tabs>
    </Box>
  );
};

export default CompanyDetailsPage;
